import { FileSearch, Search } from "lucide-react";
import { useState } from "react";
import { ContractDetailDrawer } from "@/components/contracts/ContractDetailDrawer";
import { ContractsTable } from "@/components/contracts/ContractsTable";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import type { SearchContractsResult } from "@/lib/coach-manager/client";
import type { Contract } from "@/lib/coach-manager/types";

interface ContractSearchResultsProps {
  result: SearchContractsResult | undefined;
  isPending: boolean;
  error: Error | null;
  /**
   * The BU instance the current `result` was searched against — Contract
   * objects don't carry their own BU id, so this is threaded down from the
   * search form's last submission to let the detail drawer's "Initiate Copy"
   * button know which BU the contract came from.
   */
  buInstanceId?: string | null;
}

/**
 * Renders every state of a contract search: idle prompt, loading, a
 * route/transport-level error, a Coach-Manager-level failure (`ok: false`),
 * zero matches, or the matched contracts as a matrix table — clicking a
 * Contract ID opens the full detail in a side drawer.
 */
export function ContractSearchResults({
  result,
  isPending,
  error,
  buInstanceId,
}: ContractSearchResultsProps) {
  const { label } = useBuInstanceLabel();
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  if (isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error.message} />;
  }

  if (!result) {
    return (
      <EmptyState
        icon={Search}
        title="Search for a contract"
        description={`Pick a ${label} and enter search criteria above.`}
      />
    );
  }

  if (!result.ok) {
    return <ErrorState message={result.error} />;
  }

  if (result.contracts.length === 0) {
    return (
      <EmptyState
        icon={FileSearch}
        title="No contracts found"
        description="Try different search criteria."
      />
    );
  }

  return (
    <>
      <ContractsTable contracts={result.contracts} onSelect={setSelectedContract} />
      <ContractDetailDrawer
        contract={selectedContract}
        buInstanceId={buInstanceId ?? null}
        onClose={() => setSelectedContract(null)}
      />
    </>
  );
}
